/** Arrange where a unit goes next, and start it when it gets there.
 *
 *  Everything a deployment needs is known in an office well before the boat
 *  goes out: the farm, roughly where on it, how deep, what the unit should be
 *  doing. What was missing was anywhere to put that before the fact, so a move
 *  meant somebody at a screen at the appointed hour — the same mistake as
 *  configuring a node from a boat, with the work placed at the worst possible
 *  moment for no reason other than that nothing recorded the intention.
 *
 *  Nothing here touches the node. The new deployment is created `planned`, and
 *  a planned mission is not one a node can upload to — so an arrangement made
 *  on Monday cannot quietly swallow Tuesday's recordings.
 *
 *  Two ways for it to start, and they are the same mechanism:
 *
 *  - With a time, it starts by itself the first time the unit makes contact at
 *    or after it. Waiting for contact rather than firing on a clock is the
 *    whole design: a mission's window decides which recordings are
 *    attributable, and a boat is late far more often than it is early. Opening
 *    the new mission at 13:00 sharp would claim an hour of van as ocean data at
 *    a site nobody had reached.
 *  - With no time, nothing starts it but a person. That is the safer order when
 *    someone will be standing at the site: plug in, check data is arriving,
 *    start it, check data is still arriving — two things you can see instead of
 *    one that happens while you are on a boat. "Start it now" simply sets the
 *    time to now, so there is no second activation path needing its own proof.
 *
 *  Lives here rather than on the node page because a mission *is* a deployment
 *  episode, so "what happens after this one" belongs next to the one it
 *  replaces. It appears on both, and fetches the compute itself rather than
 *  being handed a `pending` prop — the cache is keyed by path, so on the node
 *  page, which has already loaded the same document, this costs nothing.
 */
import { useState } from 'react'
import {
  ApiError, api, fmtTime, patchMission, redeploy, transitionMission,
} from '../api'
import { invalidate, useApi } from '../useApi'
import { Link } from '../router'
import { Field } from './bits'
import { LocalityPicker, type Place } from './LocalityPicker'

interface ComputeMissions {
  missions: {
    mission_id: string
    site_name: string | null
    state: string
    activate_after: string | null
  }[]
}

export function Move({ compute, onSaved }: {
  compute: string
  onSaved: () => void
}) {
  const path = `/v1/computes/${encodeURIComponent(compute)}`
  const q = useApi<ComputeMissions>(path)

  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState<Place | null>(null)
  const [depth, setDepth] = useState('')
  const [when, setWhen] = useState('')
  const [purpose, setPurpose] = useState<'deployment' | 'test'>('deployment')
  const [busy, setBusy] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Any planned mission, with or without a time on it. One arranged for
  // someone to start by hand is the one they most need to see.
  const pending = q.data?.missions.find((m) => m.state === 'planned') ?? null
  const currentSite =
    q.data?.missions.find((m) => m.state === 'active')?.site_name ?? null

  // Nothing to show until we know whether a move is already arranged —
  // offering "arrange a move" and then replacing it with "moving" a moment
  // later reads as the first click having done something it did not.
  if (!q.data) return null

  const done = () => {
    invalidate('/v1/fleet')
    invalidate('/v1/computes')
    // And re-run our own query. invalidate() empties the cache; it does not
    // re-fetch for a component already mounted, and the parent's onSaved
    // reloads the parent's query, not this one. Without this the card went on
    // showing the state from before the write -- so after arranging a move,
    // "Start it now" did not appear until the page was refreshed by hand.
    q.reload()
    onSaved()
  }

  /** The current revision, for If-Match. Read immediately before the write
   *  rather than held: two people correcting the same deployment from two
   *  boats must collide loudly, not silently. */
  const revisionOf = async (missionId: string) =>
    (await api.get<{ revision: number }>(
      `/v1/missions/${encodeURIComponent(missionId)}`)).revision

  const cancelMove = async () => {
    if (!pending) return
    setBusy(true)
    setErr(null)
    try {
      // Disarmed, not deleted. The site, the position and the depth someone
      // typed are still right if the trip is merely postponed, and a planned
      // mission with no time on it waits for a person instead of a clock.
      await patchMission(pending.mission_id, await revisionOf(pending.mission_id),
        {}, ['activate_after'])
      done()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'Could not cancel.')
    } finally {
      setBusy(false)
    }
  }

  const startNow = async () => {
    if (!pending) return
    setBusy(true)
    setErr(null)
    try {
      // Not a separate "activate" call. Setting the time to now hands the move
      // to the node's own check-in, which is what stamps the real start, closes
      // the old deployment against it and repoints the node. The endpoint that
      // transitions a mission directly does none of those things.
      await patchMission(pending.mission_id, await revisionOf(pending.mission_id),
        { activate_after: new Date().toISOString() })
      done()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'Could not start it.')
    } finally {
      setBusy(false)
    }
  }

  const discardMove = async () => {
    if (!pending) return
    setBusy(true)
    setErr(null)
    try {
      // Archived rather than deleted: it is a true record that a deployment was
      // arranged and did not happen, and it leaves 'planned' so nothing can
      // start it.
      await transitionMission(pending.mission_id, 'archived')
      done()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'Could not discard it.')
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    if (!place) return
    setBusy(true)
    setErr(null)
    try {
      await redeploy({
        compute_id: compute,
        ...(place.locality_no
          ? { locality_no: place.locality_no }
          : { site: place.name }),
        ...(place.lat != null ? { lat: place.lat } : {}),
        ...(place.lon != null ? { lon: place.lon } : {}),
        ...(depth.trim() !== '' ? { depth_m: Number(depth) } : {}),
        // Local wall-clock in, instant out — the same conversion every other
        // date field here makes. Empty means nothing starts it but a person,
        // which is the right answer when you will be standing at the site.
        ...(when ? { activate_after: new Date(when).toISOString() } : {}),
        purpose,
      })
      setOpen(false)
      setPlace(null)
      setDepth('')
      setWhen('')
      done()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'Could not arrange this.')
    } finally {
      setBusy(false)
    }
  }

  if (pending) {
    return (
      <div className="card">
        <div className="card-head" style={{ paddingLeft: 0, paddingRight: 0 }}>
          <h2>Moving next</h2>
          <span className="badge">planned</span>
        </div>
        <p style={{ marginTop: 4 }}>
          Going to <strong>{pending.site_name ?? 'a new site'}</strong>
          {pending.activate_after && <> from {fmtTime(pending.activate_after)}</>}.
        </p>
        <p className="faint">
          {pending.activate_after ? (
            <>
              It keeps recording to its current deployment until then. The move
              happens the first time the unit checks in after that time, so if
              it is still in transit nothing changes until it arrives.
            </>
          ) : (
            <>
              No time is set, so nothing will start this except you. That is the
              safer order when you are at the site: plug in, check data is
              arriving, then start it and check data is still arriving.
            </>
          )}
        </p>
        <div className="row" style={{ marginTop: 12 }}>
          {/* Sets the time to now, which the node acts on at its next check-in
              — about ten seconds. Deliberately the same path a scheduled move
              takes, rather than a second way to start a deployment that would
              need proving separately. */}
          <button className="btn sm" disabled={busy}
            onClick={() => void startNow()}>
            {busy ? 'Working…' : 'Start it now'}
          </button>
          <Link className="btn ghost sm"
            to={`/missions/${encodeURIComponent(pending.mission_id)}`}>
            Open it
          </Link>
          {pending.activate_after && (
            <button className="btn ghost sm" disabled={busy}
              onClick={() => void cancelMove()}>
              {busy ? 'Working…' : 'Cancel the time'}
            </button>
          )}
          {!discarding && (
            <button className="btn ghost sm"
              onClick={() => { setErr(null); setDiscarding(true) }}>
              Discard this plan
            </button>
          )}
        </div>
        {discarding && (
          <div className="notice" style={{ marginTop: 12 }}>
            <strong>Discard this planned move?</strong>
            <p style={{ margin: '8px 0 0' }}>
              The site, position and depth on it are lost and there is no undo.
              {pending.activate_after
                ? <> If the trip is only postponed, <em>Cancel the time</em> keeps
                    all of it and waits for you instead.</>
                : <> It is not going to start on its own, so there is no hurry to
                    clear it.</>}
            </p>
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn danger sm" disabled={busy}
                onClick={() => void discardMove()}>
                {busy ? 'Working…' : 'Discard it'}
              </button>
              <button className="btn ghost sm" onClick={() => setDiscarding(false)}>
                Keep it
              </button>
            </div>
          </div>
        )}
        {err && <div className="notice bad" style={{ marginTop: 12 }}>{err}</div>}
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-head" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <h2>Move to a new site</h2>
      </div>

      {!open ? (
        <>
          <p className="faint" style={{ marginTop: 4 }}>
            Set up the next deployment now — where it is going, how deep, and
            whether it starts on a time or when you say so. The unit carries on
            where it is until then.
          </p>
          <button className="btn sm" onClick={() => { setErr(null); setOpen(true) }}>
            Arrange a move
          </button>
        </>
      ) : (
        <div className="stack" style={{ marginTop: 8, gap: 14 }}>
          <LocalityPicker value={place} onChange={setPlace} />

          <Field label="Depth (m)"
            hint="How deep the hydrophone will hang. Correctable from the deployment page once someone has been out.">
            <input className="num" inputMode="decimal" value={depth}
              onChange={(e) => setDepth(e.target.value)} placeholder="15" />
          </Field>

          <Field label="From"
            hint="Leave empty and nothing starts it but you — the safer choice if you will be at the site. Set a time and it starts itself the first time the unit checks in after it.">
            <input type="datetime-local" value={when}
              onChange={(e) => setWhen(e.target.value)} />
          </Field>

          {/* Asked, not inherited. A unit being moved is very often coming off
              a bench test, and carrying that mission's `test` across would make
              the first real deployment of every node silently absent from the
              ocean-impact figures. */}
          <Field label="Counts towards impact"
            hint="A test does not contribute to anything Phoniq claims about the ocean.">
            <select value={purpose}
              onChange={(e) => setPurpose(e.target.value as 'deployment' | 'test')}>
              <option value="deployment">Yes — a real deployment</option>
              <option value="test">No — a test</option>
            </select>
          </Field>

          {currentSite && place && (
            <div className="notice">
              This closes the deployment at <strong>{currentSite}</strong> when
              the move happens. Its recordings stay where they are.
            </div>
          )}

          {err && <div className="notice bad">{err}</div>}
          <div className="row">
            <button className="btn sm" disabled={busy || !place}
              onClick={() => void save()}>
              {busy ? 'Arranging…' : 'Arrange the move'}
            </button>
            <button className="btn ghost sm" disabled={busy}
              onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
